import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { sendLinearUpdate } from "~/features/configurator/Linear/api/sendLinearConfiguratorUpdate";
import { sendLinearDataReset } from "~/features/configurator/Linear/api/sendLinearStepReset";
import { sendLinearStepChange } from "~/features/configurator/Linear/api/sendLinearStepChange";
import { sendLinearResetConfigurator } from "~/features/configurator/Linear/api/sendLinearResetConfigurator";
import { sendLinearAddToCart } from "~/features/configurator/Linear/api/sendLinearAddToCart";
import { SummaryData } from "~/features/configurator/Linear/v2/configuration/kllt/summary/KLLTUnitSummaryData";
import { type RootState } from "~/app/store";
import { LinearSummaryParams } from "~/features/configurator/Linear/components/LinearSummaryParams";
import {
  type ConfiguratorPageDefinition,
  type StepHardcodedData,
} from "~/features/configurator/ConfiguratorPageDefinition";
import { KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import {
  BASE_DESIGNATION_SEPARATOR,
  CENTRAL_CONNECTORS_VALUE,
  MANUAL_CONNECTORS_VALUE,
} from "~/features/configurator/Linear/constants";
import * as UnitCstic from "~/features/configurator/Linear/v2/configuration/kllt/KLLTUnitCstics";
import * as CarriageCstics from "~/features/configurator/Carriage/v2/configuration/kwlt/KWLTCarriageCstics";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { getCsticsValue, join } from "~/features/configurator/common/v2/helper";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import type { LinearConfiguration, LinearSubstep } from "~/features/configurator/Linear/models";
import { validateFromDefinition } from "~/features/configurator/common/v2/validation";
import { findSelectedTileIndex } from "~/features/configurator/Linear/utils";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";
import {
  type CarriageKmatSummaryData,
  prepareSubStepsInfo,
  type RailKmatSummaryData,
  transformCarriageSummaryDataToV2,
  transformRailSummaryDataToV2,
  transformSummaryDataToV2,
  transformToSelectionData,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import {
  getStep1SummaryData,
  getStep2SummaryData,
  getStep2SummaryDataRail,
  getStep3SummaryDataCarriage,
} from "~/features/configurator/Linear/v2/configuration/kllt/summary/KLLTUnitSummaryDataProvider";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { selectRailAmount } from "~/features/configurator/Linear/redux/selectors";
import { CsticNamingHelper } from "./CsticNamingHelper";

const UNIT_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      let step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const substeps = [SubstepType.SystemLayout, SubstepType.Size];
      step1Data = [...prepareSubStepsInfo(summaryDataProvider, substeps)!, ...step1Data];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[0].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];
      const guidewayData: RailKmatSummaryData[] = getStep2SummaryDataRail(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(2),
        gaName: summaryDataProvider.getGaStepName(2),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step2Data.map(transformSummaryDataToV2),
          },
          transformRailSummaryDataToV2(guidewayData),
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[1].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);

      const railAmount = selectRailAmount(state);
      const carriageData: CarriageKmatSummaryData[] = getStep3SummaryDataCarriage(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(3),
        gaName: summaryDataProvider.getGaStepName(3),
        tables: [transformCarriageSummaryDataToV2(carriageData)],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[2].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
];

export const KLLT_UNIT_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.System,
  productCode: KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.unit.name",
  },
  apiCalls: {
    update: sendLinearUpdate,
    reset: sendLinearResetConfigurator,
    stepChange: sendLinearStepChange,
    stepReset: sendLinearDataReset,
    addToCart: sendLinearAddToCart,
  },
  summary: SummaryData,
  summaryParams: LinearSummaryParams,
  isAddToCartEnabled: (state: RootState) => {
    const kmats = state.linearConfigurator.kmats;

    const conflicts = state.linearConfigurator.conflicts;
    const validations = [0, 1, 2].map(
      (step) =>
        state.linearConfigurator.steps[step].substeps?.every(
          (substep) =>
            validateFromDefinition(state.linearConfigurator.substeps[substep] as LinearSubstep, kmats, conflicts)
              .substepValid,
        ) ?? false,
    );
    const addToCartEnabled = validations.every((validation) => validation);
    return addToCartEnabled;
  },
  isSaveEnabled: (state) => {
    return KLLT_UNIT_DEFINITION.isAddToCartEnabled(state);
  },
  canQueryAvailability: (state) => {
    return KLLT_UNIT_DEFINITION.isAddToCartEnabled(state);
  },
  canQueryPrice: (state) => {
    return KLLT_UNIT_DEFINITION.isAddToCartEnabled(state);
  },
  stepHardcodedData: UNIT_CONFIGURATOR_STEP_DATA,
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [materialName, sizeCode, version] = getCsticsValue(
      kmat,
      UnitCstic.DesignType_Series,
      UnitCstic.DesignType_SizeCode,
      UnitCstic.DesignType_Version,
    );

    const designation = join("-", join("", materialName, sizeCode), version);
    return designation || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "kllt-system",
  modelAndUpdatePreprocessor: (model) => {
    const mainKmat = getMainKmat({ kmats: model.kmats });
    const namingHelper = new CsticNamingHelper(mainKmat.cstics);

    namingHelper.updateNames(UnitCstic.RailLocatingFaceSegment1, {
      label: "configurator.unit.cstic.railLocatingFace1.name",
      value: {
        label: (value) => `configurator.unit.cstic.railLocatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(UnitCstic.RailLocatingFaceSegment2, {
      label: "configurator.unit.cstic.railLocatingFace2.name",
      value: {
        label: (value) => `configurator.unit.cstic.railLocatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(UnitCstic.CarriageLocatingFaceSegment1, {
      label: "configurator.unit.cstic.carriageLocatingFace1.name",
      value: {
        label: (value) => `configurator.unit.cstic.carriageLocatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(UnitCstic.CarriageLocatingFaceSegment2, {
      label: "configurator.unit.cstic.carriageLocatingFace2.name",
      value: {
        label: (value) => `configurator.unit.cstic.carriageLocatingFace.${value}.name`,
      },
    });

    const railKmats = model.kmats[KmatType.SubRail];
    railKmats.forEach(({ cstics }) => {
      const namingHelper = new CsticNamingHelper(cstics);

      namingHelper.updateNames(UnitCstic.AL, {
        label: "configurator.unit.section.al.name",
      });

      namingHelper.updateNames(UnitCstic.AR, {
        label: "configurator.unit.section.ar.name",
      });

      namingHelper.updateNames(UnitCstic.PartSizing, {
        label: "configurator.unit.cstic.partSizing.name",
        value: {
          label: (value) => `configurator.unit.cstic.partSizing.${value}.name`,
        },
      });

      namingHelper.updateNames(UnitCstic.PartAmount, {
        label: "configurator.unit.cstic.partAmount.name",
        value: {
          label: (value) => `configurator.unit.cstic.partAmount.${value}.name`,
        },
      });

      namingHelper.updateNames(UnitCstic.GuidewayLength, {
        label: "configurator.unit.cstic.guidewayLength.name",
      });
    });

    const carriageKmats = model.kmats[KmatType.SubCarriage];
    carriageKmats.forEach(({ cstics }) => {
      const namingHelper = new CsticNamingHelper(cstics);

      namingHelper.updateNames(CarriageCstics.CarriageDesign, {
        label: "configurator.unit.step.referenceCarriage.name",
        info: "configurator.unit.step.configureCarriage.section.carriageType.info",
        value: {
          label: (value) => `configurator.unit.cstic.carriageType.${value}.name`,
        },
      });

      namingHelper.updateNames(CarriageCstics.KitsLeft, {
        label: "configurator.unit.cstic.kit.left.name",
        info: "configurator.unit.cstic.kit.left.info.KLLT",
      });

      namingHelper.updateNames(CarriageCstics.KitsCenter, {
        label: "configurator.unit.cstic.kit.center.name",
        info: "configurator.unit.cstic.kit.center.info.KLLT",
      });

      namingHelper.updateNames(CarriageCstics.KitsRight, {
        label: "configurator.unit.cstic.kit.right.name",
        info: "configurator.unit.cstic.kit.left.info.KLLT",
      });

      namingHelper.updateNames(CarriageCstics.LubricationConnector, {
        label: "configurator.unit.cstic.lubricationConnector.name",
        value: {
          imageUrl: (value) => `configurator.unit.cstic.lubricationConnector.${value}.image`,
        },
      });

      namingHelper.updateNames(CarriageCstics.LubricationConnectorOrientation, {
        // label: "configurator.unit.cstic.lubricationConnectorOrientation.name",
        // Deliberate change - Because both rows have the same name in summary
        label: "configurator.unit.cstic.lubricationConnectorStatus.name",
        info: "configurator.unit.cstic.configureCarriage.section.lubricationConnector.info",
        value: {
          label: (value) => `configurator.unit.cstic.lubricationConnectorStatus.${value}.name`,
        },
      });

      const mapping: Record<string, string> = {
        [MANUAL_CONNECTORS_VALUE]: "configurator.unit.cstic.configureCarriage.section.lubricationConnector.manual.name",
        [CENTRAL_CONNECTORS_VALUE]:
          "configurator.unit.cstic.configureCarriage.section.lubricationConnector.central.name",
      };
      namingHelper.updateNames(CarriageCstics.LubricationConnectorType, {
        value: {
          label: (value) => mapping[value],
        },
      });
    });

    return model;
  },
  postDefinitionProcessor: (model) => {
    model.allowedSteps = calculateAllowedSteps(model);
    return model;
  },
} as const;

export type NamingProps = {
  label?: string;
  description?: string;
  info?: string;
  value?: {
    label?: (value: string) => string;
    imageUrl?: (value: string) => string;
  };
};
function pageIsConfigured(model: LinearConfiguration, page: number): boolean {
  return (model.steps[page]!.substeps ?? []).every((substepType) => {
    const substep = model.substeps[substepType];

    substep.selectedTile = findSelectedTileIndex(substep as LinearSubstep, model.kmats);

    return validateFromDefinition(
      substep as LinearSubstep,
      model.kmats,
      (model as LinearConfiguration).conflictedCstics ?? [],
    ).substepValid;
  });
}

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const canVisitStep2 = pageIsConfigured(model, 0);
  const canVisitStep3 = pageIsConfigured(model, 1) && canVisitStep2;
  const canVisitStep4 = pageIsConfigured(model, 2) && canVisitStep3;

  const allowedSteps = [
    [true, canVisitStep2, canVisitStep3, canVisitStep4],
    [true, true, canVisitStep3, canVisitStep4],
    [true, true, true, canVisitStep4],
    [true, true, true, true],
  ];

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(KLLT_UNIT_DEFINITION);
